import React, { useState } from 'react';
import { Loader2, Send, X, Info, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Compact inline widget shown on each station card: lets the user propose
 * a custom stream URL. The submission is reviewed by the admin before being
 * applied to the station.
 */
const StreamUrlSubmission = ({ stationId, stationName, apiBase, token }) => {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => { setUrl(''); setNote(''); setExpanded(false); };

  const handleSubmit = async () => {
    if (!url.trim()) {
      toast.error('Εισάγετε URL');
      return;
    }
    if (!(url.startsWith('http://') || url.startsWith('https://'))) {
      toast.error('Το URL πρέπει να ξεκινά με http:// ή https://');
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(`${apiBase}/api/stream-url-submissions/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ station_id: stationId, url: url.trim(), note: note.trim() || null }),
      });
      const data = await resp.json();
      if (resp.ok) {
        toast.success(data.message || 'Το URL στάλθηκε στον admin');
        reset();
      } else {
        toast.error(data.detail || 'Αποτυχία υποβολής');
      }
    } catch (e) {
      toast.error('Σφάλμα δικτύου');
    } finally {
      setLoading(false);
    }
  };

  if (!expanded) {
    return (
      <button
        data-testid={`submit-url-toggle-${stationId}`}
        onClick={() => setExpanded(true)}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-[11px] font-medium transition-colors"
        title="Προτείνετε το δικό σας stream URL για αυτόν τον σταθμό"
      >
        <ExternalLink className="w-3 h-3" />
        {t('saStreamUrlSuggestion')}
      </button>
    );
  }

  return (
    <div data-testid={`submit-url-form-${stationId}`} className="bg-indigo-500/5 border border-indigo-500/30 rounded-lg p-2.5 mt-1.5 space-y-2">
      <div className="flex items-start gap-2 text-[11px] text-indigo-200/80">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-indigo-400" />
        <div>
          <div className="font-semibold text-indigo-300">Αιτηθείτε να χρησιμοποιηθεί το δικό σας stream URL</div>
          <div className="mt-0.5">
            Το URL θα αποσταλεί για έλεγχο στον admin. Αν εγκριθεί, θα χρησιμοποιηθεί για τον σταθμό <span className="text-indigo-100 font-medium">{stationName}</span>.
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <input
          data-testid={`submit-url-input-${stationId}`}
          type="url"
          placeholder="https://stream.example.com/live"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          className="flex-1 bg-gray-900/80 border border-white/10 rounded-md px-2.5 py-1.5 text-xs text-gray-100 placeholder:text-gray-600 focus:outline-none focus:border-indigo-400"
        />
      </div>
      <textarea
        data-testid={`submit-url-note-${stationId}`}
        placeholder="Προαιρετική σημείωση (π.χ. 'Νέο URL μετά τη μετακόμιση του streamer')"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        maxLength={500}
        className="w-full bg-gray-900/80 border border-white/10 rounded-md px-2.5 py-1.5 text-xs text-gray-100 placeholder:text-gray-600 focus:outline-none focus:border-indigo-400 resize-none"
      />
      <div className="flex justify-end gap-2">
        <button
          data-testid={`submit-url-cancel-${stationId}`}
          onClick={reset}
          disabled={loading}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs text-gray-400 hover:text-gray-200"
        >
          <X className="w-3 h-3" />
          Άκυρο
        </button>
        <button
          data-testid={`submit-url-send-${stationId}`}
          onClick={handleSubmit}
          disabled={loading || !url.trim()}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-medium transition-colors"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          Υποβολή
        </button>
      </div>
    </div>
  );
};

export default StreamUrlSubmission;
